import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { search as searchApi } from '@/utils/api';
import {
  Search as SearchIcon, ArrowRight, Folder, Users, Bug, BookOpen, FileText, Settings,
  LayoutDashboard, Columns, UserPlus, Sparkles, Calendar, CalendarCheck, Video, Inbox,
  Mic, Gift, Globe, ScrollText, BarChart3, FolderOpen, Sun as TodayIcon, CreditCard,
} from 'lucide-react';

/**
 * Global command palette — ⌘K / Ctrl+K.
 * Shows navigation commands always + live search results typed 2+ chars.
 */
const NAV_COMMANDS = [
  { id: 'nav-today', label: 'Go to Today', hint: 'today', icon: TodayIcon, to: '/admin/today' },
  { id: 'nav-dashboard', label: 'Go to Dashboard', icon: LayoutDashboard, to: '/admin' },
  { id: 'nav-inbox', label: 'Go to Inbox', icon: Inbox, to: '/admin/inbox' },
  { id: 'nav-pipeline', label: 'Go to Pipeline', icon: Columns, to: '/admin/pipeline' },
  { id: 'nav-clients', label: 'Go to Clients', icon: Users, to: '/admin/clients' },
  { id: 'nav-leads', label: 'Go to Leads', icon: UserPlus, to: '/admin/leads' },
  { id: 'nav-wizards', label: 'Go to Wizards', icon: Sparkles, to: '/admin/wizards' },
  { id: 'nav-forms', label: 'Go to Forms', icon: FileText, to: '/admin/forms' },
  { id: 'nav-documents', label: 'Go to Documents', icon: FolderOpen, to: '/admin/documents' },
  { id: 'nav-calendar', label: 'Go to Calendar', icon: Calendar, to: '/admin/calendar' },
  { id: 'nav-delivery', label: 'Go to Delivery Calendar', icon: CalendarCheck, to: '/admin/delivery' },
  { id: 'nav-zoom', label: 'Go to Zoom Calls', icon: Video, to: '/admin/zoom' },
  { id: 'nav-meetings', label: 'Go to Meetings (notes)', icon: Mic, to: '/admin/meetings' },
  { id: 'nav-kb', label: 'Go to Knowledge Base', icon: BookOpen, to: '/admin/kb' },
  { id: 'nav-reports', label: 'Go to Reports & NPS', icon: BarChart3, to: '/admin/reports' },
  { id: 'nav-referrals', label: 'Go to Referrals', icon: Gift, to: '/admin/referrals' },
  { id: 'nav-minisites', label: 'Go to Mini-sites', icon: Globe, to: '/admin/minisites' },
  { id: 'nav-audit', label: 'Go to Audit Log', icon: ScrollText, to: '/admin/audit' },
  { id: 'nav-settings', label: 'Open Settings', icon: Settings, to: '/admin/settings' },
  { id: 'nav-profile', label: 'Profile & change password', icon: Settings, to: '/admin/profile' },
  { id: 'nav-settings-bank', label: 'Settings → Bank Accounts', icon: CreditCard, to: '/admin/settings/bank-accounts' },
  { id: 'nav-settings-integrations', label: 'Settings → Integrations', icon: Settings, to: '/admin/settings/integrations' },
  { id: 'nav-settings-zoom', label: 'Settings → Zoom', icon: Video, to: '/admin/settings/zoom' },
];

const RESULT_ICONS = {
  project: Folder, client: Users, bug: Bug, kb: BookOpen, sow: FileText,
};

export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ(''); setResults([]); setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      searchApi.query(q.trim())
        .then(r => setResults(r.data?.results || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  const filteredNav = useMemo(() => {
    const lc = q.trim().toLowerCase();
    if (!lc) return NAV_COMMANDS.slice(0, 8);
    return NAV_COMMANDS.filter(c =>
      c.label.toLowerCase().includes(lc) || (c.hint && c.hint.toLowerCase().includes(lc))
    ).slice(0, 8);
  }, [q]);

  // Flat list for keyboard nav: nav commands first, then search results
  const flat = useMemo(() => [
    ...filteredNav.map(c => ({ kind: 'nav', ...c })),
    ...results.map(r => ({ kind: 'result', ...r })),
  ], [filteredNav, results]);

  useEffect(() => { setActiveIdx(0); }, [q, results.length]);

  const exec = (item) => {
    if (!item) return;
    if (item.kind === 'nav') navigate(item.to);
    else if (item.url) navigate(item.url);
    onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, flat.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') { e.preventDefault(); exec(flat[activeIdx]); return; }
  };

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-xl rounded-card overflow-hidden animate-fade-in shadow-2xl"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
          <SearchIcon size={16} style={{ color: 'var(--muted)' }} />
          <input
            ref={inputRef}
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: 'var(--ink)' }}
            placeholder="Jump to page, client, project, bug… (Esc to close)"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            data-testid="command-palette-input"
          />
          <span className="text-[10px] px-1.5 py-0.5 rounded font-mono" style={{ background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--border)' }}>Esc</span>
        </div>

        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-2">
          {filteredNav.length > 0 && (
            <div className="mb-2">
              <p className="px-4 pb-1 text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--muted)' }}>Navigate</p>
              {filteredNav.map((c, i) => {
                const idx = i;
                const Icon = c.icon || ArrowRight;
                const active = idx === activeIdx;
                return (
                  <button
                    key={c.id}
                    data-idx={idx}
                    onClick={() => exec({ kind: 'nav', ...c })}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-left text-sm transition-colors"
                    style={{ background: active ? 'var(--bg)' : 'transparent', color: 'var(--ink)' }}
                    data-testid={`cp-nav-${c.id}`}
                  >
                    <Icon size={13} style={{ color: active ? '#006080' : 'var(--muted)' }} />
                    <span className="flex-1 truncate">{c.label}</span>
                    {active && <ArrowRight size={12} style={{ color: '#006080' }} />}
                  </button>
                );
              })}
            </div>
          )}

          {q.trim().length >= 2 && (
            <div>
              <p className="px-4 pb-1 pt-1 text-[10px] uppercase tracking-wider font-bold flex items-center gap-2" style={{ color: 'var(--muted)' }}>
                Search results
                {loading && <span className="text-[9px] font-normal">searching…</span>}
              </p>
              {!loading && results.length === 0 && (
                <p className="px-4 py-3 text-sm" style={{ color: 'var(--muted)' }}>No matches.</p>
              )}
              {results.map((r, i) => {
                const idx = filteredNav.length + i;
                const Icon = RESULT_ICONS[r.type] || Folder;
                const active = idx === activeIdx;
                return (
                  <button
                    key={`${r.type}-${r.id}`}
                    data-idx={idx}
                    onClick={() => exec({ kind: 'result', ...r })}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-left text-sm transition-colors"
                    style={{ background: active ? 'var(--bg)' : 'transparent', color: 'var(--ink)' }}
                    data-testid={`cp-result-${r.type}-${r.id}`}
                  >
                    <Icon size={13} style={{ color: active ? '#006080' : 'var(--muted)' }} />
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{r.title}</p>
                      {r.subtitle && <p className="text-[10px] truncate" style={{ color: 'var(--muted)' }}>{r.subtitle}</p>}
                    </div>
                    <span className="text-[9px] uppercase tracking-wider font-bold" style={{ color: 'var(--muted)' }}>{r.type}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 px-4 py-2 text-[10px] border-t" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          <span className="ml-auto">{flat.length} item{flat.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
}
